import React, { useContext, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import {
	commonValueFormatter,
	formatNum,
	getChartDateFormatString,
	getRatioText,
	handleDownload
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useOption } from '@/pages/dashboard/utils/customHook';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { isArray, isEmpty, isFunction } from 'lodash-es';
import { usePCRevenueColumns } from '../pcConsoleRevenue/components/PcRevenue';

function Revenue(props: any) {
	const contextObj = useContext(FilterContext);
	const {
		data: defaultData = [],
		enName,
		cnName,
		defaultActive = 1,
		otherOption,
		otherConfig,
		barAndLineFormatterFunc,
		renderFormatterFunc,
		isShowLegend = false,
		isHideEyesIcon = false
	} = props;
	const { filterVal: formValue } = contextObj || {};
	const { formatMessage, locale } = useIntl();
	const [iseye, setIseye] = useState(isHideEyesIcon ? false : true);
	const { config: configSet } = props;
	const { data: dataSource, loading, errorMsg: error } = defaultData?.MonetizationRevenueData || {};
	const { componentMetricsConfig } = configSet || {};
	// const { decimal = 0, isPercent = false, type = 'decimals', percentDecimals = 1 } = configSet?.chart ?? {};
	const data = dataSource?.revenue;
	const { timeType = 'Day' } = formValue;
	const revenueData = useMemo(() => {
		if (!isArray(data) || isEmpty(data)) return [];
		const dataSource = data?.map(item => {
			const { revenue, arppu, payer_ratio, dau } = item;
			return {
				date: moment(item.date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
				system: '',
				metrics: {
					revenue: {
						value: revenue?.today,
						dod: '',
						sdlw: '',
						change: revenue?.this_change
					},
					dau: {
						value: dau?.today,
						dod: '',
						sdlw: '',
						change: dau?.this_change
					},
					arppu: {
						value: arppu?.today,
						dod: '',
						sdlw: '',
						change: arppu?.this_change
					},
					payer_ratio: {
						value: payer_ratio?.today,
						dod: '',
						sdlw: '',
						change: payer_ratio?.this_change
					}
				}
			};
		});
		return dataSource;
	}, [data]);
	const { tableOption, chart, tableData, downloadData } = usePCRevenueColumns(
		revenueData,
		componentMetricsConfig,
		timeType,
		true,
		true
	);
	const isEn = locale === 'en-US';
	const chartTitle = isEn ? enName : cnName;
	const timeTitle: { [key in string]: string | unknown } = useMemo(() => {
		return {
			Day: formatMessage({ id: 'monetization_revenue_DAU' }),
			Week: formatMessage({ id: 'monetization_revenue_WAU' }),
			Month: formatMessage({ id: 'monetization_revenue_MAU' })
		};
	}, [formatMessage]);

	const defaultConfig = useMemo(() => {
		return {
			bar: {
				formatterFunc: (params: any) =>
					barAndLineFormatterFunc
						? barAndLineFormatterFunc?.(params)
						: formatterFunc(params, revenueData, timeType, timeTitle, componentMetricsConfig, isEn),
				setOtherConfigFunc: (opt: { xAxis: { axisLabel: { formatter: (value: any) => any } }[] }) => {
					opt.xAxis[0].axisLabel.formatter = value => getChartDateFormatString(timeType, value);
					return opt;
				}
			},
			line: {
				formatterFunc: (params: any) =>
					barAndLineFormatterFunc
						? barAndLineFormatterFunc?.(params)
						: formatterFunc(params, revenueData, timeType, timeTitle, componentMetricsConfig, isEn),
				setOtherConfigFunc: (opt: { xAxis: { axisLabel: { formatter: (value: any) => any } }[]; series: any[] }) => {
					opt.xAxis[0].axisLabel.formatter = value => getChartDateFormatString(timeType, value);
					opt.series = opt.series?.map(item => {
						return {
							...item,
							stack: '',
							showSymbol: true
						};
					});
					return opt;
				}
			}
		};
	}, [barAndLineFormatterFunc, revenueData, timeType, timeTitle, componentMetricsConfig, isEn]);
	const config = useMemo(() => {
		if (otherConfig) return otherConfig;
		return defaultConfig;
	}, [otherConfig, defaultConfig]);

	const options = useMemo(() => {
		if (otherOption) return otherOption;
		return {
			chart,
			table: {
				option: tableOption,
				data: tableData,
				downloadData
			}
		};
	}, [otherOption, chart, tableOption, tableData, downloadData]);
	const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(options);

	const renderList = [
		{
			type: 'eyes',
			svgType: iseye ? 'open-eyes' : 'eyes-closed',
			content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
			isHideCom: true,
			isHideIcon: isHideEyesIcon
		},
		{
			type: 'barChart',
			svgType: 'column-chart',
			content: formatMessage({ id: 'common_bar_chart' }),
			component: (
				<BarChart
					key={locale}
					height={340}
					isShowLegend={isShowLegend}
					legendData={xAxisData.length > 0 ? legendData : []}
					xAxisData={xAxisData || []}
					series={xAxisData.length > 0 ? barSeries : []}
					formatterFunc={config?.bar?.formatterFunc}
					setOtherConfigFunc={(opt: any) => {
						if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
						if (!isHideEyesIcon) {
							opt = addChartLabel(opt, {
								formatter: isFunction(renderFormatterFunc)
									? renderFormatterFunc
									: (params: { value: string | number }) => {
											const result = formatNum(params?.value, false);
											return result;
									  },
								show: iseye,
								formatNum: isFunction(renderFormatterFunc)
									? renderFormatterFunc
									: (value: string | number) => {
											return formatNum(value, false);
									  }
							});
						}
						opt = xAxisHighLight(opt, {
							formatter: (params: { value: string }) => {
								return getChartDateFormatString(timeType, params.value);
							}
						});
						return opt;
					}}
				/>
			)
		},
		{
			type: 'lineChart',
			svgType: 'discount-chart',
			content: formatMessage({ id: 'common_line_chart' }),
			component: (
				<LineChart
					height={340}
					key={locale}
					isShowLegend={isShowLegend}
					legendData={xAxisData.length > 0 ? legendData : []}
					xAxisData={xAxisData || []}
					series={xAxisData.length > 0 ? lineSeries : []}
					formatterFunc={config?.line?.formatterFunc}
					setOtherConfigFunc={(opt: any) => {
						if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
						if (!isHideEyesIcon) {
							opt = addChartLabel(opt, {
								formatter: isFunction(renderFormatterFunc)
									? renderFormatterFunc
									: (params: { value: string | number }) => {
											const result = formatNum(params?.value, false);
											return result;
									  },
								show: iseye,
								formatNum: isFunction(renderFormatterFunc)
									? renderFormatterFunc
									: (value: string | number) => {
											return formatNum(value, false);
									  }
							});
						}
						opt = xAxisHighLight(opt, {
							formatter: (params: { value: string }) => {
								return getChartDateFormatString(timeType, params.value);
							}
						});
						return opt;
					}}
				/>
			)
		},
		{
			type: 'sheet',
			svgType: 'sheet',
			content: formatMessage({ id: 'common_table' }),
			component: <MiniTable height={340} loading={false} tableOption={options?.table?.option} tableList={options?.table?.data} />
		}
	];

	return (
		<CardBox
			className='register-table'
			height={340}
			bordered={false}
			titleCom={chartTitle}
			renderList={renderList || []}
			defaultAvtive={defaultActive}
			setCurrentDataType={(type: any) => {
				if (type === 'eyes') {
					setIseye(!iseye);
				}
			}}
			downloadClick={async () => {
				handleDownload(options?.table, chartTitle);
			}}
			isLoading={loading}
			isError={error}
			isNodata={isNodata}
		/>
	);
}

export default Revenue;

const formatterFunc = (
	params: { axisValue: any }[],
	revenueData: any[],
	timeType = 'Day',
	timeTitle: { [x: string]: unknown },
	componentMetricsConfig: { [x: string]: unknown }[],
	isEn: boolean
) => {
	let temp = {
		className: 'monetization-revenue-bubble',
		title: moment(`${params[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
		data: [] as any,
		hasRatio: true,
		hasDot: false
	};
	params.forEach(({ color, axisValue }: any) => {
		const it = revenueData?.find(originItem => originItem.date === axisValue)?.metrics || {};
		const ratio: any = componentMetricsConfig.map(key => key.metricKey);
		// eslint-disable-next-line semi-spacing
		for (let i = 0; i < ratio.length; i++) {
			const { text, status } = getRatioText(it[ratio[i]]?.change);
			const item = componentMetricsConfig?.[i];
			temp.data.push({
				name: ratio[i] === 'dau' ? timeTitle[timeType] : isEn ? item?.enName : item?.cnName,
				metricsNum: commonValueFormatter(item?.type, it[ratio[i]]?.value, item?.decimal),
				ratioNum: text,
				ratioStatus: status,
				color
			});
		}
	});
	return getEchartTooltipText(temp);
};
